import { useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import qs from 'qs';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';

import GetOrganisationUnitTreeForLinkedIntegrationQuery from '../models/get-organisation-unit-tree-for-linked-integration-query';
import GetOrganisationUnitTreeQuery from '../models/get-organisation-unit-tree-query';

const baseUrl = '/api/v1/OrganisationUnit';

export enum OrganisationUnitQueryPaths {
  GetOrganisationUnitTree = 'GetOrganisationUnitTree',
  GetMyOrganisationUnitTree = 'GetMyOrganisationUnitTree',
  GetOrganisationUnitTreeForLinkedIntegration = 'GetOrganisationUnitTreeForLinkedIntegration',
  GetOrganisationUnitTreeWithFaults = 'GetOrganisationUnitTreeWithFaults',
  GetOrganisationUnitTreeForLocation = 'GetOrganisationUnitTreeForLocation',
}

const getOrganisationUnitTree = async (
  axiosInstance: AxiosInstance,
  query: GetOrganisationUnitTreeQuery,
): Promise<OrganisationTreeNode[]> => {
  const queryString = qs.stringify(query, { allowDots: true });
  const response = await axiosInstance.get(`${baseUrl}/${OrganisationUnitQueryPaths.GetOrganisationUnitTree}?${queryString}`);
  return response.data;
};

const getMyOrganisationUnitTree = async (
  axiosInstance: AxiosInstance,
  query: GetOrganisationUnitTreeQuery,
): Promise<OrganisationTreeNode[]> => {
  const queryString = qs.stringify(query, { allowDots: true });
  const response = await axiosInstance.get(`${baseUrl}/${OrganisationUnitQueryPaths.GetMyOrganisationUnitTree}?${queryString}`);
  return response.data;
};

const getOrganisationUnitTreeForLinkedIntegration = async (
  axiosInstance: AxiosInstance,
  body: GetOrganisationUnitTreeForLinkedIntegrationQuery,
): Promise<OrganisationTreeNode[]> => {
  const response = await axiosInstance.post(`${baseUrl}/${OrganisationUnitQueryPaths.GetOrganisationUnitTreeForLinkedIntegration}`, body);
  return response.data;
};

const getOrganisationUnitTreeWithFaults = async (axiosInstance: AxiosInstance): Promise<OrganisationTreeNode[]> => {
  const response = await axiosInstance.get(`${baseUrl}/${OrganisationUnitQueryPaths.GetOrganisationUnitTreeWithFaults}`);
  return response.data;
};

const getOrganisationUnitTreeForLocation = async (
  axiosInstance: AxiosInstance,
  organisationUnitId: string,
): Promise<OrganisationTreeNode[]> => {
  const response = await axiosInstance.get(
    `${baseUrl}/${OrganisationUnitQueryPaths.GetOrganisationUnitTreeForLocation}/${organisationUnitId}`,
  );
  return response.data;
};

export const useGetOrganisationUnitTree = (query: GetOrganisationUnitTreeQuery) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [OrganisationUnitQueryPaths.GetOrganisationUnitTree, query],
    queryFn: () => getOrganisationUnitTree(authenticatedInstance, query),
  });
};

export const useGetMyOrganisationUnitTree = (query: GetOrganisationUnitTreeQuery) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [OrganisationUnitQueryPaths.GetMyOrganisationUnitTree, query],
    queryFn: () => getMyOrganisationUnitTree(authenticatedInstance, query),
  });
};

export const useLazyGetOrganisationUnitTreeForLinkedIntegration = (body: GetOrganisationUnitTreeForLinkedIntegrationQuery) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [OrganisationUnitQueryPaths.GetOrganisationUnitTreeForLinkedIntegration, body],
    queryFn: () => getOrganisationUnitTreeForLinkedIntegration(authenticatedInstance, body),
    enabled: false,
  });
};

export const useGetOrganisationUnitTreeWithFaults = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [OrganisationUnitQueryPaths.GetOrganisationUnitTreeWithFaults],
    queryFn: () => getOrganisationUnitTreeWithFaults(authenticatedInstance),
  });
};

export const useLazyGetOrganisationUnitTreeForLocation = (organisationUnitId: string) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [OrganisationUnitQueryPaths.GetOrganisationUnitTreeForLocation, organisationUnitId],
    queryFn: () => getOrganisationUnitTreeForLocation(authenticatedInstance, organisationUnitId),
    enabled: false,
  });
};
